import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lightbulb, ArrowRight, RotateCcw, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { db } from "@/api/db";
import { FACTOR_INFO } from "@/components/disc/discProfileData";
import { PROFILE_NARRATIVES } from "@/components/disc/discNarratives";

import SpecialSituations from "@/components/disc/SpecialSituations";
import NarrativeSection from "@/components/disc/NarrativeSection";

const FACTORS_ORDER = ["D", "I", "S", "C"];

const NARRATIVE_KEYS = [
  "vantagens",
  "desvantagens",
  "estiloLideranca",
  "estiloComunicacao",
  "reacaoSobPressao",
  "tomadaDeDecisoes",
  "relacionamentos",
  "organizacaoPlanejamento",
];

function getScoreLevel(pct) {
  if (pct >= 65) return "high";
  if (pct >= 40) return "mid";
  return "low";
}

function getScoreLevelLabel(pct) {
  if (pct >= 65) return "Alto";
  if (pct >= 40) return "Moderado";
  return "Baixo";
}

/* ── Factor Breakdown ─────────────────────────────────────── */
function FactorBreakdown({ percentages }) {
  return (
    <div className="space-y-5">
      {FACTORS_ORDER.map((f) => {
        const info = FACTOR_INFO[f];
        const pct = percentages[f] || 0;
        const level = getScoreLevel(pct);
        const levelLabel = getScoreLevelLabel(pct);
        const interpretation = info.scoreInterpretation?.[level];

        return (
          <div key={f}>
            {/* Header row */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: info.color }} />
                <span
                  className="text-sm font-medium"
                  style={{ fontFamily: "Work Sans, sans-serif", color: "#2E3233" }}
                >
                  {info.name}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <span
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                  style={{ background: `${info.color}12`, color: info.color }}
                >
                  {levelLabel}
                </span>
                <span
                  className="text-sm font-semibold tabular-nums w-9 text-right"
                  style={{ color: "#2E3233" }}
                >
                  {pct}%
                </span>
              </div>
            </div>

            {/* Bar */}
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#F2F4F4" }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: info.color }}
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              />
            </div>

            {/* Interpretation */}
            {interpretation && (
              <p
                className="text-[13px] leading-relaxed mt-2.5"
                style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
              >
                {interpretation}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Section Heading (reusable) ───────────────────────────── */
function SectionHeading({ title, subtitle }) {
  return (
    <div className="mb-5">
      <h3
        className="text-base font-semibold"
        style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
      >
        {title}
      </h3>
      {subtitle && (
        <p className="text-xs mt-0.5" style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

/* ── Main Component ───────────────────────────────────────── */
function getRetakeDaysRemaining(completedAt) {
  if (!completedAt) return 0;
  const completed = new Date(completedAt);
  if (isNaN(completed.getTime())) return 0;
  const unlockDate = new Date(completed);
  unlockDate.setDate(unlockDate.getDate() + 90);
  const now = new Date();
  const diff = Math.ceil((unlockDate - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

export default function DiscResult({ result, user, saving, completedAt, onRetake }) {
  const [vocationalResult, setVocationalResult] = useState(null);

  useEffect(() => {
    if (user) {
      db.entities.UserProfile.filter({ user_email: user.email }, "-created_date", 1)
        .then(profiles => {
          const p = profiles[0];
          if (p?.vocational_completed && p?.vocational_result) {
            setVocationalResult(p.vocational_result);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  const {
    percentages,
    primaryFactor,
    secondaryFactor,
    combinedProfile,
    profileLabel,
    profileData,
    specialSituations,
  } = result;

  const primaryInfo = FACTOR_INFO[primaryFactor];
  const secondaryInfo = FACTOR_INFO[secondaryFactor];

  const firstName = user?.full_name ? user.full_name.split(" ")[0] : "Você";
  const narrativeKey = combinedProfile in PROFILE_NARRATIVES
    ? combinedProfile
    : `${primaryFactor}${secondaryFactor.toLowerCase()}`;
  const narratives = PROFILE_NARRATIVES[narrativeKey] || null;

  function replaceNamePlaceholder(text) {
    if (!text) return "";
    return text.replace(/\{name\}/g, firstName);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      className="px-4 py-10"
    >
      <div className="max-w-2xl mx-auto space-y-8">

        {/* ── Header ──────────────────────────────────────── */}
        <div className="text-center">
          <p
            className="text-xs font-medium uppercase tracking-widest mb-3"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
          >
            Relatório Comportamental
          </p>
          <h1
            className="text-2xl font-bold mb-2"
            style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
          >
            {firstName}, este é o seu perfil DISC
          </h1>
          <p
            className="text-sm"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
          >
            Resultado completo com base nas suas 24 respostas.
          </p>
        </div>

        {/* ── Profile Card ────────────────────────────────── */}
        <div
          id="perfil"
          className="rounded-2xl p-6"
          style={{
            background: "#ffffff",
            border: "1px solid #E5E9EA",
            boxShadow: "0 4px 16px -2px rgba(0,0,0,0.06), 0 1px 3px 0 rgba(0,0,0,0.04)",
          }}
        >
          <div className="flex items-start gap-4 mb-5">
            <div className="flex gap-1.5">
              {combinedProfile.split("").map((char, i) => {
                const factor = char.toUpperCase();
                const info = FACTOR_INFO[factor];
                const isUpper = char === char.toUpperCase();
                return (
                  <div
                    key={i}
                    className="rounded-xl flex items-center justify-center font-bold"
                    style={{
                      width: isUpper ? 48 : 36,
                      height: isUpper ? 48 : 36,
                      background: info?.color || "#999",
                      color: "#ffffff",
                      fontSize: isUpper ? 20 : 14,
                    }}
                  >
                    {char.toUpperCase()}
                  </div>
                );
              })}
            </div>
            <div className="flex-1 pt-0.5">
              <p
                className="text-[11px] font-medium uppercase tracking-wider mb-1"
                style={{ color: "#99A6AB" }}
              >
                Perfil combinado
              </p>
              <h2
                className="text-xl font-bold"
                style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
              >
                {profileLabel}
              </h2>
            </div>
          </div>

          <div className="flex gap-4 mb-4">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: primaryInfo.color }} />
              <span className="text-xs" style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}>
                Primário: {primaryInfo.name}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: secondaryInfo.color }} />
              <span className="text-xs" style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}>
                Secundário: {secondaryInfo.name}
              </span>
            </div>
          </div>

        </div>

        {/* ── Brief personal summary ──────────────────────── */}
        {profileData && (
          <p
            className="text-sm leading-[1.75]"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
          >
            <strong style={{ color: "#2E3233" }}>{firstName}</strong>, seu perfil predominante é{" "}
            <strong style={{ color: "#2E3233" }}>{primaryInfo.name}</strong> com traços de{" "}
            <strong style={{ color: "#2E3233" }}>{secondaryInfo.name}</strong>.{" "}
            {profileData.description}
          </p>
        )}

        {/* ── Factor Breakdown ────────────────────────────── */}
        <div
          id="fatores"
          className="rounded-2xl bg-white p-6"
          style={{ border: "1px solid #E5E9EA", boxShadow: "0 1px 2px 0 rgba(0,0,0,0.04)" }}
        >
          <SectionHeading
            title="Seus fatores DISC"
            subtitle="Cada fator revela como você tende a agir no trabalho. Não existe score bom ou ruim."
          />
          <FactorBreakdown percentages={percentages} />
        </div>

        {/* ── Special Situations (alerts) ─────────────────── */}
        <SpecialSituations situations={specialSituations} />

        {/* ── Strengths + Development Areas ───────────────── */}
        {profileData && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className="rounded-2xl p-5 bg-white"
              style={{ border: "1px solid #E5E9EA", boxShadow: "0 1px 2px 0 rgba(0,0,0,0.04)" }}
            >
              <h4
                className="text-sm font-semibold mb-4"
                style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
              >
                Pontos Fortes
              </h4>
              <ul className="space-y-2.5">
                {profileData.strengths.map((s, i) => (
                  <li
                    key={i}
                    className="text-sm flex items-start gap-2.5"
                    style={{ fontFamily: "Work Sans, sans-serif", color: "#2E3233" }}
                  >
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: "#00DCA4" }} />
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="rounded-2xl p-5 bg-white"
              style={{ border: "1px solid #E5E9EA", boxShadow: "0 1px 2px 0 rgba(0,0,0,0.04)" }}
            >
              <h4
                className="text-sm font-semibold mb-4"
                style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
              >
                Áreas de Desenvolvimento
              </h4>
              <ul className="space-y-2.5">
                {profileData.developmentAreas.map((a, i) => (
                  <li
                    key={i}
                    className="text-sm flex items-start gap-2.5"
                    style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
                  >
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: "#D9DBDA" }} />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* ── Narratives ──────────────────────────────────── */}
        {narratives && (
          <div id="narrativas" className="space-y-3">
            <SectionHeading
              title="Análise Detalhada"
              subtitle={`Narrativas contextuais do perfil ${profileLabel} (${combinedProfile}).`}
            />
            {NARRATIVE_KEYS.map((key) => {
              const text = narratives[key];
              if (!text) return null;
              return (
                <NarrativeSection
                  key={key}
                  sectionKey={key}
                  content={replaceNamePlaceholder(text)}
                />
              );
            })}
          </div>
        )}

        {/* ── Cross with Vocational ───────────────────────── */}
        {vocationalResult && (
          <div
            className="rounded-2xl p-6"
            style={{ background: "#FAFBFB", border: "1px solid #E5E9EA" }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: "#EAFAF6" }}
              >
                <Lightbulb className="w-4 h-4" style={{ color: "#00664C" }} />
              </div>
              <h3
                className="text-base font-semibold"
                style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
              >
                Cruzamento com o Radar Profissional
              </h3>
            </div>
            <p
              className="text-sm leading-[1.7] mb-4"
              style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
            >
              Seu Radar apontou <strong style={{ color: "#2E3233" }}>{vocationalResult}</strong> como profissão de maior afinidade, e seu perfil DISC é <strong style={{ color: "#2E3233" }}>{profileLabel} ({combinedProfile})</strong>.
              Isso sugere que você se destacaria nessa área usando seu estilo {primaryInfo.name.toLowerCase()} como força principal e {secondaryInfo.name.toLowerCase()} como apoio.
            </p>
            <Link to={createPageUrl("VocationalTest")}>
              <span
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all hover:shadow-sm"
                style={{
                  fontFamily: "Work Sans, sans-serif",
                  background: "#ffffff",
                  color: "#00664C",
                  border: "1px solid #E5E9EA",
                }}
              >
                Ver meu Radar Profissional
                <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>
        )}

        {/* ── Interview Tips ──────────────────────────────── */}
        {profileData?.interviewTips && (
          <div
            className="rounded-2xl p-6 bg-white"
            style={{ border: "1px solid #E5E9EA", boxShadow: "0 1px 2px 0 rgba(0,0,0,0.04)" }}
          >
            <h3
              className="text-base font-semibold mb-4"
              style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
            >
              Dicas para Entrevista
            </h3>
            <ul className="space-y-2.5 mb-5">
              {profileData.interviewTips.map((tip, i) => (
                <li
                  key={i}
                  className="text-sm flex items-start gap-2.5"
                  style={{ fontFamily: "Work Sans, sans-serif", color: "#2E3233" }}
                >
                  <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: "#00DCA4" }} />
                  {tip}
                </li>
              ))}
            </ul>
            <Link to={createPageUrl("InterviewSimulator")}>
              <span
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all hover:shadow-sm"
                style={{
                  fontFamily: "Work Sans, sans-serif",
                  background: "#FAFBFB",
                  color: "#2E3233",
                  border: "1px solid #E5E9EA",
                }}
              >
                Praticar no Simulador de Entrevista
                <ArrowRight className="w-3 h-3" style={{ color: "#99A6AB" }} />
              </span>
            </Link>
          </div>
        )}

        {/* ── Retake + Saving ────────────────────────────── */}
        <div className="flex flex-col items-center gap-3 pt-4">
          {saving && (
            <p
              className="text-center text-xs"
              style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
            >
              Salvando resultado...
            </p>
          )}
          {onRetake && (() => {
            const daysRemaining = getRetakeDaysRemaining(completedAt);
            const locked = daysRemaining > 0;
            return (
              <div className="flex flex-col items-center gap-1.5">
                <button
                  onClick={locked ? undefined : onRetake}
                  disabled={locked}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-medium transition-all"
                  style={{
                    fontFamily: "Work Sans, sans-serif",
                    background: locked ? "#F2F4F4" : "#FAFBFB",
                    color: locked ? "#C8CED0" : "#646C6F",
                    border: `1px solid ${locked ? "#E5E9EA" : "#E5E9EA"}`,
                    cursor: locked ? "not-allowed" : "pointer",
                  }}
                >
                  {locked
                    ? <Lock className="w-3.5 h-3.5" />
                    : <RotateCcw className="w-3.5 h-3.5" />
                  }
                  Refazer teste
                </button>
                {locked && (
                  <p
                    className="text-[11px]"
                    style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
                  >
                    disponível em {daysRemaining} {daysRemaining === 1 ? "dia" : "dias"}
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </motion.div>
  );
}
